import { Injectable } from '@angular/core';
import { GetBusinessAddressGQL } from '@generated/queries';
import { firstValueFrom } from 'rxjs';

/**
 * Shared service to access business timezone from session addresses.
 * Uses GetBusinessAddress and returns the timezone from the first address that has one.
 */
@Injectable({
  providedIn: 'root',
})
export class BusinessTimezoneService {
  private cachedTimeZone: string | null | undefined = undefined;

  constructor(private getBusinessAddressGQL: GetBusinessAddressGQL) {}

  /**
   * Returns business timezone (e.g. 'UTC+05:00') from session addresses.
   * Scans the address list and picks the first address that has a timeZone.
   * Result is cached for the session.
   */
  async getBusinessTimeZone(): Promise<string | null> {
    if (this.cachedTimeZone !== undefined) {
      return this.cachedTimeZone;
    }
    try {
      const res = await firstValueFrom(this.getBusinessAddressGQL.fetch());
      const addresses = res?.data?.session?.addresses ?? [];
      const firstWithTimeZone = addresses.find(
        (a) => a != null && a.timeZone != null && a.timeZone !== ''
      );
      this.cachedTimeZone = firstWithTimeZone?.timeZone ?? null;
      return this.cachedTimeZone;
    } catch {
      this.cachedTimeZone = null;
      return null;
    }
  }

  /** Clear cached timezone (e.g. after logout or business switch). */
  clearCache(): void {
    this.cachedTimeZone = undefined;
  }
}
